import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Grid, Stack, Typography, IconButton,
  Select, MenuItem, Button, TextField, Alert, Chip, Tooltip,
  Drawer, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Avatar, Badge, InputAdornment, alpha, LinearProgress,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TodayIcon from '@mui/icons-material/Today';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import apiService from '../../services/apiServices';
import globals from '../../services/globals';

const TYPE_META = {
  1: { name: 'Vjetor',           hex: '#10b981', bg: '#ecfdf5', text: '#065f46', light: '#d1fae5' },
  6: { name: 'Me pagesë',        hex: '#3b82f6', bg: '#eff6ff', text: '#1e40af', light: '#dbeafe' },
  4: { name: 'Pa pagesë',        hex: '#6b7280', bg: '#f9fafb', text: '#374151', light: '#f3f4f6' },
  2: { name: 'Mjekësor',         hex: '#f59e0b', bg: '#fffbeb', text: '#92400e', light: '#fef3c7' },
  3: { name: 'Martesor',         hex: '#ec4899', bg: '#fdf2f8', text: '#9d174d', light: '#fce7f3' },
  5: { name: 'Jashtëzakonshëm',  hex: '#8b5cf6', bg: '#f5f3ff', text: '#5b21b6', light: '#ede9fe' },
};

const WEEKDAYS = ['Hën', 'Mar', 'Mër', 'Enj', 'Pre', 'Sht', 'Die'];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtDate(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function isWeekend(d) { return d.getDay() === 0 || d.getDay() === 6; }
function isToday(d) { return fmtDate(d) === fmtDate(new Date()); }
function getInitials(f, l) { return `${(f||'').charAt(0)}${(l||'').charAt(0)}`.toUpperCase(); }
function avatarColor(id) {
  const colors = ['#3b82f6','#10b981','#f59e0b','#ec4899','#8b5cf6','#06b6d4','#ef4444','#14b8a6'];
  return colors[(parseInt(id,10)||0) % colors.length];
}

// ── Stat Card ──────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color, bgGradient, sub }) {
  return (
    <Card elevation={0} sx={{
      flex: 1, minWidth: 150,
      borderRadius: 2.5,
      border: '1px solid',
      borderColor: alpha(color, 0.18),
      background: bgGradient,
      overflow: 'hidden',
      position: 'relative',
    }}>
      <Box sx={{
        position: 'absolute', top: -18, right: -18,
        width: 80, height: 80, borderRadius: '50%',
        bgcolor: alpha(color, 0.08),
      }} />
      <CardContent sx={{ p: '16px 20px !important' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ color, lineHeight: 1, mb: 0.4 }}>
              {value}
            </Typography>
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {label}
            </Typography>
            {sub !== undefined && (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                {sub}
              </Typography>
            )}
          </Box>
          <Box sx={{
            p: 1.2, borderRadius: 2,
            bgcolor: alpha(color, 0.12),
            display: 'flex', alignItems: 'center',
          }}>
            {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function LeaveCalendar() {
  const [activeTab, setActiveTab]     = useState(0);
  const [month, setMonth]             = useState(startOfMonth(new Date()));
  const [employees, setEmployees]     = useState([]);
  const [holidays, setHolidays]       = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading]         = useState(true);

  const [empFilter, setEmpFilter]     = useState('all');
  const [typeFilter, setTypeFilter]   = useState('all');
  const [deptFilter, setDeptFilter]   = useState('all');
  const [approvedOnly, setApprovedOnly] = useState(false);
  const [searchQ, setSearchQ]         = useState('');

  const [newOpen, setNewOpen]         = useState(false);
  const [empForNew, setEmpForNew]     = useState('');
  const [typeForNew, setTypeForNew]   = useState(1);
  const [startSel, setStartSel]       = useState(null);
  const [endSel, setEndSel]           = useState(null);
  const [msg, setMsg]                 = useState('');

  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [drawerDate, setDrawerDate]   = useState(null);
  const [presenceDept, setPresenceDept] = useState('all');
  const [monthAtt, setMonthAtt]         = useState({}); // "workerId:date" → { checkIn, workMin, location }

  useEffect(() => {
    Promise.all([
      apiService().get('workers'),
      apiService().get('departments'),
      apiService().get('branches'),
      apiService().get('holidays'),
    ]).then(([w, d, b, h]) => {
      if (w?.success) {
        setEmployees(w.data || []);
        try {
          const user = globals().getLogedInUser();
          const uid = user?.data?.id ?? user?.data?.Id;
          if (uid && w.data?.some(x => (x.Id??x.id) === uid)) setEmpForNew(uid);
        } catch(_) {}
      }
      if (d?.success) setDepartments(d.data || []);
      if (h?.success) setHolidays(h.data || []);
      setLoading(false);
    });
  }, []);

  const loadHolidays = () =>
    apiService().get('holidays').then(res => { if (res?.success) setHolidays(res.data||[]); });

  // Load real attendance for current month (both tabs)
  useEffect(() => {
    const mm = `${month.getFullYear()}-${String(month.getMonth()+1).padStart(2,'0')}`;
    apiService().query('getMonthAttendance', `month=${mm}`)
      .then(r => { setMonthAtt(r?.success && r.data?.[0] ? r.data[0] : {}); })
      .catch(() => setMonthAtt({}));
  }, [month]);

  const days = useMemo(() => {
    const start = startOfMonth(month);
    const startDay = (start.getDay() + 6) % 7;
    return Array.from({ length: 42 }, (_, i) => addDays(addDays(start, -startDay), i));
  }, [month]);

  const holidaysByDay = useMemo(() => {
    const map = new Map();
    holidays.forEach(h => {
      const s = new Date(h.StartDate), e = new Date(h.EndDate);
      for (let d = new Date(s); d <= e; d = addDays(d, 1)) {
        const key = fmtDate(d);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(h);
      }
    });
    return map;
  }, [holidays]);

  const getDeptName = emp => {
    const id = emp?.Department ?? emp?.DepartmentId;
    return departments.find(d => String(d.id??d.Id) === String(id))?.name ||
           departments.find(d => String(d.Id) === String(id))?.Name ||
           emp?.Department || '';
  };

  const getEmpName = empOrId => {
    if (typeof empOrId === 'object') return `${empOrId.FirstName||''} ${empOrId.LastName||''}`.trim();
    const e = employees.find(x => String(x.Id??x.id) === String(empOrId));
    return e ? `${e.FirstName||''} ${e.LastName||''}`.trim() : '—';
  };

  const getHolidayEmpId = h => h.WorkerId ?? h.Employee;

  const eventsForDay = (dateObj, extraFilter = true) => {
    const key = fmtDate(dateObj);
    return (holidaysByDay.get(key) || []).filter(h => {
      const empId = getHolidayEmpId(h);
      const emp = employees.find(e => String(e.Id??e.id) === String(empId));
      const empOk   = empFilter === 'all'  || String(empId) === String(empFilter);
      const typeOk  = typeFilter === 'all' || Number(h.HolidayType) === Number(typeFilter);
      const apprOk  = !approvedOnly || !!h.Approve;
      const deptOk  = deptFilter === 'all' || String(emp?.Department ?? emp?.DepartmentId) === String(deptFilter);
      const srchOk  = !searchQ || getEmpName(emp).toLowerCase().includes(searchQ.toLowerCase());
      return empOk && typeOk && apprOk && deptOk && (extraFilter ? srchOk : true);
    });
  };

  const stats = useMemo(() => {
    const todayKey = fmtDate(new Date());
    const onLeaveToday = new Set((holidaysByDay.get(todayKey)||[]).map(h => h.WorkerId ?? h.Employee)).size;
    const pending  = holidays.filter(h => !h.Approve).length;
    const approved = holidays.filter(h => h.Approve).length;
    return { onLeaveToday, pending, approved, total: employees.length };
  }, [holidaysByDay, holidays, employees]);

  const saveLeave = () => {
    setMsg('');
    if (!startSel || !endSel || !empForNew || !typeForNew) { setMsg('Plotëso datat, llojin dhe punëtorin.'); return; }
    apiService().post('holidays', {
      Employee: empForNew, HolidayType: typeForNew,
      StartDate: fmtDate(startSel), EndDate: fmtDate(endSel),
      Approve: false, CreatedBy: '', Description: '',
    }).then(res => {
      if (res?.success) { setMsg('Kërkesa u regjistrua.'); loadHolidays(); setStartSel(null); setEndSel(null); setNewOpen(false); }
      else setMsg('Gabim gjatë ruajtjes.');
    });
  };

  const inCurrentMonth = d => d.getMonth() === month.getMonth();

  const monthDays = useMemo(() => {
    const n = new Date(month.getFullYear(), month.getMonth()+1, 0).getDate();
    return Array.from({ length: n }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i+1));
  }, [month]);

  const presenceEmployees = useMemo(() => employees.filter(emp => {
    if (presenceDept === 'all') return true;
    return String(emp?.Department ?? emp?.DepartmentId) === String(presenceDept);
  }), [employees, presenceDept]);

  const monthTitle = month.toLocaleString('sq-AL', { month: 'long', year: 'numeric' });

  const filteredEmployees = useMemo(() =>
    employees.filter(e => !searchQ || getEmpName(e).toLowerCase().includes(searchQ.toLowerCase())),
  [employees, searchQ]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>

      {/* ── Page Header ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mb: 3 }}
        gap={2}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={{
            p: 1.2, borderRadius: 2.5,
            bgcolor: alpha('#3b82f6', 0.1),
            display: 'flex', alignItems: 'center',
          }}>
            <CalendarMonthIcon sx={{ fontSize: 24, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} letterSpacing={-0.5} lineHeight={1.2}>
              Kalendari i Pushimeve
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
              Menaxho dhe monitoro pushimet e punëtorëve
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setStartSel(null); setEndSel(null); setNewOpen(true); }}
          sx={{
            fontWeight: 700, borderRadius: 2, px: 2.5,
            textTransform: 'none', fontSize: '0.875rem',
            boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
            flexShrink: 0,
          }}
        >
          Shto pushim
        </Button>
      </Stack>

      {/* ── Stat Cards ── */}
      <Stack direction={{ xs:'column', sm:'row' }} spacing={2} sx={{ mb: 3 }}>
        <StatCard
          icon={<PeopleOutlineIcon/>}
          label="Gjithsej punëtorë"
          value={stats.total}
          color="#3b82f6"
          bgGradient="linear-gradient(135deg, #eff6ff 0%, #fff 100%)"
        />
        <StatCard
          icon={<BeachAccessIcon/>}
          label="Sot në pushim"
          value={stats.onLeaveToday}
          color="#10b981"
          bgGradient="linear-gradient(135deg, #ecfdf5 0%, #fff 100%)"
          sub={stats.total ? `${Math.round(stats.onLeaveToday/stats.total*100)}% e stafit` : ''}
        />
        <StatCard
          icon={<PendingActionsIcon/>}
          label="Pritje aprovimi"
          value={stats.pending}
          color="#f59e0b"
          bgGradient="linear-gradient(135deg, #fffbeb 0%, #fff 100%)"
        />
        <StatCard
          icon={<CheckCircleOutlineIcon/>}
          label="Pushime aprovuara"
          value={stats.approved}
          color="#8b5cf6"
          bgGradient="linear-gradient(135deg, #f5f3ff 0%, #fff 100%)"
        />
      </Stack>

      {/* ── Main Card ── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        {loading && <LinearProgress sx={{ borderRadius: '12px 12px 0 0' }} />}

        {/* Header Bar */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 0 }}>
          <Stack direction={{ xs:'column', sm:'row' }} justifyContent="space-between" alignItems={{ xs:'flex-start', sm:'center' }} gap={1.5}>

            {/* Month Navigation */}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton size="small"
                onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth()-1, 1))}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, '&:hover': { bgcolor: 'grey.100' } }}>
                <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />
              </IconButton>
              <Box
                onClick={() => setMonth(startOfMonth(new Date()))}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                  px: 1.5, py: 0.6, borderRadius: 1.5,
                  '&:hover': { bgcolor: 'grey.100' }, transition: 'background .15s',
                }}>
                <TodayIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={800} letterSpacing={-0.5} textTransform="capitalize">
                  {monthTitle}
                </Typography>
              </Box>
              <IconButton size="small"
                onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth()+1, 1))}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, '&:hover': { bgcolor: 'grey.100' } }}>
                <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Stack>

            {/* Search */}
            {activeTab === 0 && (
              <TextField
                size="small"
                placeholder="Kërko punëtor..."
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }}/></InputAdornment>,
                  sx: { borderRadius: 2, fontSize: '0.85rem' },
                }}
                sx={{ width: { xs: '100%', sm: 210 } }}
              />
            )}
          </Stack>

          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              mt: 2,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', minHeight: 44 },
              '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
            }}>
            <Tab label="Kalendari" icon={<CalendarMonthIcon sx={{ fontSize: 16 }}/>} iconPosition="start" />
            <Tab label="Prezenca mujore" icon={<GridViewIcon sx={{ fontSize: 16 }}/>} iconPosition="start" />
          </Tabs>
        </Box>

        <Divider />

        {/* ══════════════════════════════════════════
            TAB 0 — CALENDAR
        ══════════════════════════════════════════ */}
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', minHeight: 620 }}>

            {/* ── Left Sidebar ── */}
            <Box sx={{
              width: 230, flexShrink: 0,
              borderRight: '1px solid', borderColor: 'divider',
              display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
            }}>
              {/* Filters */}
              <Box sx={{ px: 2, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" gap={0.6} mb={1.5}>
                  <FilterAltOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.8}>
                    Filtrat
                  </Typography>
                </Stack>
                <Stack spacing={1.2}>
                  <Select size="small" value={empFilter} onChange={e => setEmpFilter(e.target.value)} fullWidth displayEmpty
                    sx={{ fontSize: '0.8rem', borderRadius: 1.5 }}>
                    <MenuItem value="all"><em>Të gjithë punëtorët</em></MenuItem>
                    {employees.map(e => (
                      <MenuItem key={e.Id} value={String(e.Id)} sx={{ fontSize: '0.8rem' }}>
                        {e.FirstName} {e.LastName}
                      </MenuItem>
                    ))}
                  </Select>

                  <Select size="small" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} fullWidth displayEmpty
                    sx={{ fontSize: '0.8rem', borderRadius: 1.5 }}>
                    <MenuItem value="all"><em>Të gjithë llojet</em></MenuItem>
                    {Object.entries(TYPE_META).map(([k, m]) => (
                      <MenuItem key={k} value={k} sx={{ fontSize: '0.8rem' }}>
                        <Stack direction="row" alignItems="center" gap={0.8}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: m.hex, flexShrink: 0 }} />
                          {m.name}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>

                  <Select size="small" value={deptFilter} onChange={e => setDeptFilter(e.target.value)} fullWidth displayEmpty
                    sx={{ fontSize: '0.8rem', borderRadius: 1.5 }}>
                    <MenuItem value="all"><em>Të gjithë dept.</em></MenuItem>
                    {departments.map(d => {
                      const did = d.Id??d.id;
                      return <MenuItem key={did} value={String(did)} sx={{ fontSize: '0.8rem' }}>{d.Name||d.name}</MenuItem>;
                    })}
                  </Select>

                  <Button
                    size="small"
                    variant={approvedOnly ? 'contained' : 'outlined'}
                    onClick={() => setApprovedOnly(v => !v)}
                    startIcon={approvedOnly ? <CheckCircleIcon sx={{ fontSize: 14 }}/> : null}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: 1.5 }}>
                    {approvedOnly ? 'Vetëm aprovuar' : 'Të aprovuar'}
                  </Button>
                </Stack>
              </Box>

              {/* Employee List */}
              <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5 }}>
                <Typography variant="caption" fontWeight={700} color="text.disabled"
                  textTransform="uppercase" letterSpacing={0.8} sx={{ px: 0.5, mb: 1, display: 'block' }}>
                  Punëtorët ({filteredEmployees.length})
                </Typography>
                <Stack spacing={0.4}>
                  {filteredEmployees.map(emp => {
                    const empId = String(emp.Id??emp.id);
                    const selected = empFilter === empId;
                    const monthLeaves = holidays.filter(h => {
                      const s = new Date(h.StartDate), e2 = new Date(h.EndDate);
                      const ms = new Date(month.getFullYear(), month.getMonth(), 1);
                      const me = new Date(month.getFullYear(), month.getMonth()+1, 0);
                      return String(h.WorkerId ?? h.Employee) === empId && s <= me && e2 >= ms;
                    });
                    return (
                      <Box
                        key={empId}
                        onClick={() => setEmpFilter(selected ? 'all' : empId)}
                        sx={{
                          p: '7px 10px', borderRadius: 2, cursor: 'pointer',
                          bgcolor: selected ? alpha('#3b82f6', 0.1) : 'transparent',
                          border: '1px solid',
                          borderColor: selected ? alpha('#3b82f6', 0.35) : 'transparent',
                          '&:hover': { bgcolor: selected ? alpha('#3b82f6', 0.1) : 'grey.100' },
                          transition: 'all .15s',
                        }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Badge
                            badgeContent={monthLeaves.length || 0}
                            color="primary"
                            invisible={monthLeaves.length === 0}
                            sx={{ '& .MuiBadge-badge': { fontSize: '0.58rem', minWidth: 15, height: 15, top: 2, right: 2 } }}>
                            <Avatar sx={{ width: 30, height: 30, fontSize: '0.62rem', fontWeight: 800, bgcolor: avatarColor(empId) }}>
                              {getInitials(emp.FirstName, emp.LastName)}
                            </Avatar>
                          </Badge>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" alignItems="center" gap={0.5}>
                              <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {emp.FirstName} {emp.LastName}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'primary.main', bgcolor: alpha('#3b82f6', 0.1), px: 0.5, borderRadius: 0.8, lineHeight: 1.5, flexShrink: 0 }}>
                                #{empId}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {getDeptName(emp) || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>

              {/* Legend */}
              <Box sx={{ px: 2, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Typography variant="caption" fontWeight={700} color="text.disabled"
                  textTransform="uppercase" letterSpacing={0.8} sx={{ mb: 1, display: 'block' }}>
                  Legjenda
                </Typography>
                <Stack spacing={0.6}>
                  {Object.entries(TYPE_META).map(([k, m]) => (
                    <Stack key={k} direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: 0.6, bgcolor: m.hex, flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>{m.name}</Typography>
                    </Stack>
                  ))}
                  <Divider sx={{ my: 0.5 }} />
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>Prezent · Zyrë</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#7c3aed', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>Prezent · Distancë</Typography>
                  </Stack>
                </Stack>
              </Box>
            </Box>

            {/* ── Calendar Grid ── */}
            <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2.5 }, overflowX: 'auto' }}>
              {/* Weekday Headers */}
              <Grid container sx={{ mb: 1 }}>
                {WEEKDAYS.map((w, i) => (
                  <Grid key={w} item sx={{ flexBasis: '14.2857%', textAlign: 'center' }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        color: i >= 5 ? 'error.light' : 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.8,
                        fontSize: '0.68rem',
                      }}>
                      {w}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Day Cells */}
              <Grid container>
                {days.map(d => {
                  const key = fmtDate(d);
                  const dayEvents = eventsForDay(d);
                  const inMonth = inCurrentMonth(d);
                  const weekend = isWeekend(d);
                  const today = isToday(d);

                  // Presence indicators for this day
                  const presenceForDay = empFilter !== 'all'
                    ? (monthAtt[`${empFilter}:${key}`] || null)
                    : null;
                  const hasPresence = !!presenceForDay?.checkIn;

                  return (
                    <Grid key={key} item sx={{ flexBasis: '14.2857%', p: 0.35 }}>
                      <Box
                        onClick={() => {
                          if (dayEvents.length) { setDrawerDate(d); setDrawerOpen(true); }
                          else { setStartSel(d); setEndSel(d); setNewOpen(true); }
                        }}
                        sx={{
                          minHeight: { xs: 75, sm: 100 },
                          p: 0.9,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: today
                            ? 'primary.main'
                            : dayEvents.length
                            ? alpha('#3b82f6', 0.2)
                            : alpha('#e5e7eb', 0.8),
                          bgcolor: today
                            ? alpha('#3b82f6', 0.05)
                            : weekend && inMonth
                            ? alpha('#f9fafb', 1)
                            : dayEvents.length
                            ? alpha('#eff6ff', 0.5)
                            : 'background.paper',
                          opacity: inMonth ? 1 : 0.35,
                          cursor: 'pointer',
                          transition: 'all .12s ease',
                          '&:hover': {
                            borderColor: today ? 'primary.main' : 'primary.light',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            transform: 'translateY(-1px)',
                          },
                        }}>

                        {/* Day number */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                          <Box sx={{
                            width: 24, height: 24, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: today ? 'primary.main' : 'transparent',
                          }}>
                            <Typography
                              sx={{
                                fontSize: '0.78rem', fontWeight: today ? 800 : 600, lineHeight: 1,
                                color: today ? 'white' : weekend ? 'error.light' : inMonth ? 'text.primary' : 'text.disabled',
                              }}>
                              {d.getDate()}
                            </Typography>
                          </Box>
                          {dayEvents.length > 0 && (
                            <Box sx={{
                              minWidth: 18, height: 18, borderRadius: '50%',
                              bgcolor: 'primary.main', display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>
                                {dayEvents.length}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Event Chips */}
                        <Stack spacing={0.3}>
                          {dayEvents.slice(0, 3).map((h, idx) => {
                            const emp = employees.find(e => String(e.Id??e.id) === String(h.WorkerId ?? h.Employee));
                            const meta = TYPE_META[h.HolidayType] || TYPE_META[1];
                            return (
                              <Tooltip
                                key={idx}
                                title={
                                  <Box>
                                    <b>{emp ? `${emp.FirstName} ${emp.LastName}` : '—'}</b><br/>
                                    {meta.name} · {h.Approve ? '✓ Aprovuar' : '⏳ Pritje'}<br/>
                                    {h.StartDate?.substring(0,10)} – {h.EndDate?.substring(0,10)}
                                  </Box>
                                }
                                placement="top" arrow>
                                <Box sx={{
                                  display: 'flex', alignItems: 'center', gap: 0.5,
                                  px: 0.7, py: 0.25, borderRadius: 1,
                                  bgcolor: h.Approve ? meta.bg : '#fffbeb',
                                  borderLeft: `2.5px solid ${h.Approve ? meta.hex : '#f59e0b'}`,
                                }}>
                                    <Avatar sx={{ width: 13, height: 13, fontSize: '0.45rem', bgcolor: avatarColor(String(h.WorkerId ?? h.Employee)), flexShrink: 0 }}>
                                    {getInitials(emp?.FirstName, emp?.LastName)}
                                  </Avatar>
                                  <Typography sx={{
                                    fontSize: '0.6rem', fontWeight: 700,
                                    color: h.Approve ? meta.text : '#92400e',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3,
                                  }}>
                                    {emp ? `${emp.FirstName} ${emp.LastName?.charAt(0)}.` : '—'}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                          {dayEvents.length > 3 && (
                            <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary', pl: 0.5 }}>
                              +{dayEvents.length - 3} të tjerë
                            </Typography>
                          )}

                          {/* Presence badge */}
                          {hasPresence && (() => {
                            const fmtClk = (iso) => {
                              if (!iso) return '';
                              const dt = new Date(iso);
                              return `${('0'+dt.getHours()).slice(-2)}:${('0'+dt.getMinutes()).slice(-2)}`;
                            };
                            const isRemote = presenceForDay.location === 'remote';
                            const clkIn = fmtClk(presenceForDay.checkIn);
                            return (
                              <Tooltip
                                title={
                                  <Box>
                                    <b>{isRemote ? 'Prezent · Distancë' : 'Prezent · Zyrë'}</b><br/>
                                    Ardhja: {clkIn}
                                    {presenceForDay.checkOut ? ` · Dalja: ${fmtClk(presenceForDay.checkOut)}` : ' · Aktiv'}
                                  </Box>
                                }
                                placement="bottom" arrow>
                                <Box sx={{
                                  display: 'flex', alignItems: 'center', gap: 0.5,
                                  px: 0.7, py: 0.2, borderRadius: 1, mt: 0.2,
                                  bgcolor: isRemote ? '#f5f3ff' : '#ecfdf5',
                                  borderLeft: `2.5px solid ${isRemote ? '#7c3aed' : '#10b981'}`,
                                }}>
                                  <Box sx={{
                                    width: 6, height: 6, borderRadius: '50%',
                                    bgcolor: isRemote ? '#7c3aed' : '#10b981',
                                    flexShrink: 0,
                                  }} />
                                  <Typography sx={{
                                    fontSize: '0.58rem', fontWeight: 700,
                                    color: isRemote ? '#5b21b6' : '#065f46',
                                    lineHeight: 1.3,
                                  }}>
                                    {clkIn} {isRemote ? '· Dist.' : '· Zyrë'}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </Stack>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Box>
        )}

        {/* ══════════════════════════════════════════
            TAB 1 — PRESENCE GRID
        ══════════════════════════════════════════ */}
        {activeTab === 1 && (
          <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Stack direction={{ xs:'column', sm:'row' }} justifyContent="space-between" alignItems={{ xs:'flex-start', sm:'center' }} gap={1.5} sx={{ mb: 2.5 }}>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
                <Select size="small" value={presenceDept} onChange={e => setPresenceDept(e.target.value)} displayEmpty
                  sx={{ minWidth: 200, borderRadius: 1.5, fontSize: '0.85rem' }}>
                  <MenuItem value="all">Të gjithë departamentet</MenuItem>
                  {departments.map(d => {
                    const did = d.Id??d.id;
                    return <MenuItem key={did} value={String(did)}>{d.Name||d.name}</MenuItem>;
                  })}
                </Select>
                <TextField size="small" placeholder="Kërko punëtor..." value={searchQ}
                  onChange={e => setSearchQ(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }}/></InputAdornment>,
                    sx: { borderRadius: 1.5 },
                  }}
                  sx={{ width: 200 }} />
              </Stack>

              {/* Legend */}
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {[
                  { label: 'Zyrë',              bg: '#ecfdf5', color: '#065f46', dot: '#10b981' },
                  { label: 'Distancë',           bg: '#f5f3ff', color: '#7c3aed', dot: '#7c3aed' },
                  { label: 'Mungon',             bg: '#fff1f2', color: '#dc2626', dot: '#ef4444' },
                  { label: 'Pushim',             bg: '#eff6ff', color: '#1e40af', dot: '#3b82f6' },
                  { label: 'Pritje',             bg: '#fffbeb', color: '#92400e', dot: '#f59e0b' },
                  { label: 'Fund-javë',          bg: '#f9fafb', color: '#6b7280', dot: '#d1d5db' },
                ].map(l => (
                  <Stack key={l.label} direction="row" alignItems="center" gap={0.6}
                    sx={{ px: 1.2, py: 0.5, borderRadius: 5, bgcolor: l.bg, border: `1px solid ${alpha(l.dot, 0.3)}` }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: l.dot }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: l.color }}>{l.label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>

            <TableContainer component={Paper} elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, overflowX: 'auto', maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      minWidth: 210, fontWeight: 800,
                      position: 'sticky', left: 0, zIndex: 3,
                      bgcolor: '#f8fafc',
                      borderRight: '2px solid', borderColor: 'divider',
                      fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.6,
                      color: 'text.secondary',
                    }}>
                      Punëtori
                    </TableCell>
                    {monthDays.map(d => (
                      <TableCell key={fmtDate(d)} align="center" sx={{
                        px: 0.3, minWidth: 36, fontWeight: 700, fontSize: '0.65rem',
                        bgcolor: isToday(d) ? alpha('#3b82f6', 0.1) : isWeekend(d) ? '#f8fafc' : '#f8fafc',
                        color: isToday(d) ? 'primary.main' : isWeekend(d) ? 'text.disabled' : 'text.secondary',
                        borderBottom: isToday(d) ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        py: 0.8,
                      }}>
                        <Box fontWeight={800} sx={{ fontSize: '0.72rem' }}>{d.getDate()}</Box>
                        <Box sx={{ fontSize: '0.55rem', textTransform: 'uppercase', opacity: 0.8 }}>
                          {d.toLocaleString('sq-AL', { weekday: 'short' })}
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{
                      fontWeight: 800, fontSize: '0.72rem', minWidth: 62, bgcolor: '#f8fafc',
                      borderLeft: '2px solid #e5e7eb', color: '#10b981',
                      textTransform: 'uppercase', letterSpacing: 0.3,
                    }}>Prez.</TableCell>
                    <TableCell align="center" sx={{
                      fontWeight: 800, fontSize: '0.72rem', minWidth: 62, bgcolor: '#f8fafc',
                      color: '#3b82f6', textTransform: 'uppercase', letterSpacing: 0.3,
                    }}>Push.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {presenceEmployees
                    .filter(e => !searchQ || getEmpName(e).toLowerCase().includes(searchQ.toLowerCase()))
                    .map((emp, rowIdx) => {
                      const empId = String(emp.Id ?? emp.id);
                      let presentCount = 0, leaveCount = 0, attWorkMin = 0;
                      const cells = monthDays.map(d => {
                        const key = fmtDate(d);
                        const dayHols = holidaysByDay.get(key) || [];
                        const leave = dayHols.find(h => String(h.WorkerId ?? h.Employee) === empId);
                        const att = monthAtt[`${empId}:${key}`] || null;
                        if (!isWeekend(d)) {
                          if (leave) leaveCount++;
                          else if (att?.checkIn) { presentCount++; attWorkMin += att.workMin || 0; }
                        }
                        return { d, leave, att };
                      });
                      const fmtMin = (m) => m > 0 ? `${Math.floor(m/60)}:${('0'+(m%60)).slice(-2)}` : '—';
                      const fmtClk = (iso) => { if (!iso) return ''; const d2 = new Date(iso); return `${('0'+d2.getHours()).slice(-2)}:${('0'+d2.getMinutes()).slice(-2)}`; };
                      return (
                        <TableRow key={empId}
                          sx={{ '&:hover': { bgcolor: alpha('#f1f5f9', 0.8) }, transition: 'background .1s' }}>
                          <TableCell sx={{
                            position: 'sticky', left: 0, zIndex: 1,
                            bgcolor: rowIdx % 2 === 0 ? 'white' : '#fafafa',
                            borderRight: '2px solid #e5e7eb', whiteSpace: 'nowrap', py: 1,
                          }}>
                            <Stack direction="row" alignItems="center" spacing={1.2}>
                              <Avatar sx={{ width: 32, height: 32, fontSize: '0.7rem', fontWeight: 800, bgcolor: avatarColor(empId), flexShrink: 0 }}>
                                {getInitials(emp.FirstName, emp.LastName)}
                              </Avatar>
                              <Box>
                                <Stack direction="row" alignItems="center" gap={0.6}>
                                  <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: '0.83rem' }}>
                                    {emp.FirstName} {emp.LastName}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'primary.main', bgcolor: alpha('#3b82f6', 0.1), px: 0.6, borderRadius: 0.8, lineHeight: 1.6, flexShrink: 0 }}>
                                    #{empId}
                                  </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                  {getDeptName(emp) || '—'}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          {cells.map(({ d, leave, att }) => {
                            const weekend = isWeekend(d);
                            const today = isToday(d);
                            const future = d > new Date() && !isToday(d);
                            let bg, color, label = '', tip = '';
                            if (weekend) {
                              bg = '#f3f4f6'; color = '#d1d5db'; tip = 'Fund-javë';
                            } else if (leave) {
                              const m2 = TYPE_META[leave.HolidayType] || TYPE_META[1];
                              if (leave.Approve) {
                                bg = m2.light; color = m2.text;
                                label = m2.name.charAt(0).toUpperCase();
                                tip = `${m2.name} · Aprovuar`;
                              } else {
                                bg = '#fef3c7'; color = '#92400e'; label = '?';
                                tip = `${m2.name} · Pritje aprovimi`;
                              }
                            } else if (att?.checkIn) {
                              // Real attendance data
                              const isRemote = att.location === 'remote';
                              bg = isRemote ? '#f5f3ff' : '#ecfdf5';
                              color = isRemote ? '#7c3aed' : '#065f46';
                              const clkIn = fmtClk(att.checkIn);
                              label = clkIn;
                              tip = `Ardhja: ${clkIn}${att.checkOut ? ` · Dalja: ${fmtClk(att.checkOut)}` : ' · Aktiv'}${att.workMin > 0 ? ` · ${fmtMin(att.workMin)} orë` : ''}${isRemote ? ' · Distancë' : ''}`;
                            } else if (future) {
                              bg = 'transparent'; color = '#d1d5db'; tip = '';
                            } else {
                              bg = '#fff1f2'; color = '#dc2626'; tip = 'Mungon';
                              label = '×';
                            }
                            return (
                              <Tooltip key={fmtDate(d)} title={tip} placement="top" disableInteractive arrow>
                                <TableCell align="center" sx={{
                                  bgcolor: today ? alpha('#3b82f6', 0.08) : bg,
                                  color, fontWeight: 800, fontSize: '0.6rem',
                                  px: 0, py: 0.6,
                                  outline: today ? `2px solid ${alpha('#3b82f6', 0.3)}` : undefined,
                                  cursor: leave ? 'pointer' : 'default',
                                  '&:hover': leave ? { filter: 'brightness(0.9)' } : {},
                                  transition: 'filter .1s',
                                  lineHeight: 1.2,
                                }}
                                onClick={() => { if (leave) { setDrawerDate(d); setDrawerOpen(true); } }}>
                                  {label}
                                </TableCell>
                              </Tooltip>
                            );
                          })}

                          <TableCell align="center" sx={{ fontWeight: 800, color: '#10b981', borderLeft: '2px solid #e5e7eb', fontSize: '0.82rem' }}>
                            {presentCount}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 800, color: '#3b82f6', fontSize: '0.82rem' }}>
                            {leaveCount}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {presenceEmployees.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={monthDays.length + 3} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                        <Stack alignItems="center" spacing={1.5}>
                          <PeopleOutlineIcon sx={{ fontSize: 48, opacity: 0.25 }} />
                          <Typography fontWeight={600} color="text.secondary">Nuk u gjetën punëtorë.</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Card>

      {/* ══════════════════════════════════════════
          Dialog: Shto pushim
      ══════════════════════════════════════════ */}
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ elevation: 0, sx: { border: '1px solid', borderColor: 'divider', borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap={1.2}>
              <Box sx={{ p: 0.8, borderRadius: 1.5, bgcolor: alpha('#3b82f6', 0.1), display: 'flex' }}>
                <EventAvailableIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={800}>Shto kërkesë pushimi</Typography>
                <Typography variant="caption" color="text.secondary">Plotëso të dhënat e pushimit</Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={() => setNewOpen(false)}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.6} sx={{ mb: 0.8, display: 'block' }}>
                Punëtori
              </Typography>
              <Select fullWidth size="small" value={empForNew} onChange={e => setEmpForNew(e.target.value)} displayEmpty
                sx={{ borderRadius: 1.5 }}
                renderValue={val => {
                  if (!val) return <em style={{ color: '#9ca3af' }}>Zgjidh punëtorin</em>;
                  const e = employees.find(x => String(x.Id??x.id) === String(val));
                  return e ? (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: '0.58rem', bgcolor: avatarColor(String(val)) }}>
                        {getInitials(e.FirstName, e.LastName)}
                      </Avatar>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{e.FirstName} {e.LastName}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'primary.main', bgcolor: alpha('#3b82f6', 0.1), px: 0.6, borderRadius: 0.8 }}>
                        #{val}
                      </Typography>
                    </Stack>
                  ) : '—';
                }}>
                <MenuItem value=""><em>Zgjidh punëtorin</em></MenuItem>
                {employees.map(e => (
                  <MenuItem key={e.Id} value={e.Id}>
                    <Stack direction="row" alignItems="center" gap={1.2}>
                      <Avatar sx={{ width: 26, height: 26, fontSize: '0.63rem', bgcolor: avatarColor(String(e.Id)) }}>
                        {getInitials(e.FirstName, e.LastName)}
                      </Avatar>
                      <Box>
                        <Stack direction="row" alignItems="center" gap={0.7}>
                          <Typography variant="body2" fontWeight={600}>{e.FirstName} {e.LastName}</Typography>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'primary.main', bgcolor: alpha('#3b82f6', 0.1), px: 0.5, borderRadius: 0.8 }}>
                            #{e.Id ?? e.id}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">{getDeptName(e)}</Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.6} sx={{ mb: 0.8, display: 'block' }}>
                Lloji i pushimit
              </Typography>
              <Select fullWidth size="small" value={typeForNew} onChange={e => setTypeForNew(e.target.value)}
                sx={{ borderRadius: 1.5 }}>
                {Object.entries(TYPE_META).map(([k, m]) => (
                  <MenuItem key={k} value={Number(k)}>
                    <Stack direction="row" alignItems="center" gap={1.2}>
                      <Box sx={{ width: 10, height: 10, borderRadius: 0.6, bgcolor: m.hex }} />
                      {m.name}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.6} sx={{ mb: 0.8, display: 'block' }}>
                Periudha
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <TextField fullWidth size="small" type="date" label="Prej" InputLabelProps={{ shrink: true }}
                  value={startSel ? fmtDate(startSel) : ''} onChange={e => setStartSel(new Date(e.target.value))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                <TextField fullWidth size="small" type="date" label="Deri" InputLabelProps={{ shrink: true }}
                  value={endSel ? fmtDate(endSel) : ''} onChange={e => setEndSel(new Date(e.target.value))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
              </Stack>
            </Box>

            {startSel && endSel && (
              <Box sx={{
                p: 1.8, borderRadius: 2,
                bgcolor: alpha('#3b82f6', 0.05),
                border: '1px solid', borderColor: alpha('#3b82f6', 0.2),
              }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <CalendarMonthIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="body2" color="primary.main" fontWeight={700}>
                    {Math.round((endSel - startSel) / 86400000) + 1} ditë pushim
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    · {startSel.toLocaleDateString('sq-AL', { day: 'numeric', month: 'long' })}
                    {' → '}
                    {endSel.toLocaleDateString('sq-AL', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </Typography>
                </Stack>
              </Box>
            )}

            {msg && (
              <Alert severity={msg.includes('Gabim') ? 'error' : 'success'} sx={{ borderRadius: 2 }}>
                {msg}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setNewOpen(false)} sx={{ textTransform: 'none', borderRadius: 1.5, color: 'text.secondary' }}>
            Anulo
          </Button>
          <Button variant="contained" onClick={saveLeave} startIcon={<EventAvailableIcon/>}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, px: 3 }}>
            Ruaj kërkesën
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════
          Drawer: Aprovim / Detaje
      ══════════════════════════════════════════ */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: 320, sm: 420 }, borderLeft: '1px solid', borderColor: 'divider' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Drawer Header */}
          <Box sx={{
            px: 3, py: 2.5,
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            color: 'white',
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle1" fontWeight={800}>
                  {drawerDate ? drawerDate.toLocaleDateString('sq-AL', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
                </Typography>
                <Stack direction="row" alignItems="center" gap={0.6} mt={0.3}>
                  <BeachAccessIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                  <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600 }}>
                    {drawerDate ? eventsForDay(drawerDate, false).length : 0} kërkesa pushimi
                  </Typography>
                </Stack>
              </Box>
              <IconButton size="small" onClick={() => setDrawerOpen(false)}
                sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 1.5 }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          </Box>

          {/* Leave Cards */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {msg && (
              <Alert severity={msg.includes('Gabim') ? 'error' : 'success'} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMsg('')}>
                {msg}
              </Alert>
            )}
            <Stack spacing={1.5}>
              {drawerDate && eventsForDay(drawerDate, false).map(h => {
                const emp = employees.find(e => String(e.Id??e.id) === String(h.WorkerId ?? h.Employee));
                const meta = TYPE_META[h.HolidayType] || TYPE_META[1];
                const empId = String(h.WorkerId ?? h.Employee);
                return (
                  <Card key={h.Id} elevation={0} sx={{
                    border: '1px solid',
                    borderColor: h.Approve ? alpha(meta.hex, 0.3) : alpha('#f59e0b', 0.3),
                    borderRadius: 2.5, overflow: 'hidden',
                  }}>
                    {/* Top color bar */}
                    <Box sx={{ height: 4, bgcolor: h.Approve ? meta.hex : '#f59e0b' }} />
                    <CardContent sx={{ p: '16px !important' }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                        <Avatar sx={{ width: 44, height: 44, fontSize: '0.88rem', fontWeight: 800, bgcolor: avatarColor(empId) }}>
                          {getInitials(emp?.FirstName, emp?.LastName)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" alignItems="center" gap={0.7}>
                            <Typography variant="subtitle2" fontWeight={800}>
                              {emp ? `${emp.FirstName} ${emp.LastName}` : '—'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'primary.main', bgcolor: alpha('#3b82f6', 0.1), px: 0.7, py: 0.1, borderRadius: 1, flexShrink: 0 }}>
                              #{empId}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {getDeptName(emp) || '—'}
                          </Typography>
                        </Box>
                        <Chip size="small"
                          label={h.Approve ? 'Aprovuar' : 'Pritje'}
                          icon={h.Approve ? <CheckCircleIcon sx={{ fontSize: '14px !important', color: '#10b981 !important' }}/> : undefined}
                          sx={{
                            bgcolor: h.Approve ? '#ecfdf5' : '#fffbeb',
                            color: h.Approve ? '#065f46' : '#92400e',
                            fontWeight: 700, fontSize: '0.72rem',
                            border: `1px solid ${h.Approve ? alpha('#10b981',0.3) : alpha('#f59e0b',0.3)}`,
                          }} />
                      </Stack>

                      <Box sx={{ p: 1.5, bgcolor: meta.bg, borderRadius: 2, mb: 1.5, border: `1px solid ${alpha(meta.hex, 0.15)}` }}>
                        <Stack spacing={0.5}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{ width: 9, height: 9, borderRadius: 0.6, bgcolor: meta.hex, flexShrink: 0 }} />
                            <Typography variant="body2" fontWeight={700} color={meta.text}>{meta.name}</Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ pl: 2.5 }}>
                            {h.StartDate?.substring(0,10)} → {h.EndDate?.substring(0,10)}
                          </Typography>
                        </Stack>
                      </Box>

                      {!h.Approve && (
                        <Stack direction="row" spacing={1}>
                          <Button fullWidth size="small" variant="contained" color="success"
                            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                            onClick={async () => {
                              try {
                                const res = await apiService().put('holidays', { ...h, Approve: true });
                                if (res?.success) { setMsg('U aprovua me sukses.'); loadHolidays(); }
                                else setMsg('Gabim gjatë aprovimit.');
                              } catch { setMsg('Gabim gjatë aprovimit.'); }
                            }}>
                            ✓ Aprovo
                          </Button>
                          <Button fullWidth size="small" variant="outlined" color="error"
                            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                            onClick={async () => {
                              try {
                                const res = await apiService().put('holidays', { ...h, Approve: false });
                                if (res?.success) { setMsg('U refuzua.'); loadHolidays(); }
                                else setMsg('Gabim.');
                              } catch { setMsg('Gabim.'); }
                            }}>
                            ✕ Refuzo
                          </Button>
                        </Stack>
                      )}
                      {h.Approve && (
                        <Stack direction="row" alignItems="center" gap={0.8}
                          sx={{ p: 1, borderRadius: 1.5, bgcolor: '#ecfdf5', border: '1px solid', borderColor: alpha('#10b981', 0.2) }}>
                          <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                          <Typography variant="caption" color="success.main" fontWeight={700}>
                            Pushimi është aprovuar
                          </Typography>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {drawerDate && eventsForDay(drawerDate, false).length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <BeachAccessIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
                  <Typography fontWeight={600} color="text.secondary">S'ka pushime për këtë ditë.</Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
